import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, LogIn } from "lucide-react";
import BrandMark from "@/components/salnes/BrandMark";
import LanguageSelector from "@/components/salnes/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const { t } = useTranslation();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/app/dashboard";
  // Session-expired banner: shown when either the query flag is present
  // (hard-redirect from the axios interceptor) OR when the persistent
  // sessionStorage flag survives a React <ProtectedRoute> internal redirect
  // that swallows the query string.
  const [expired, setExpired] = useState(() => {
    if (typeof window === "undefined") return false;
    return (
      params.get("expired") === "1" ||
      window.sessionStorage?.getItem("salnes_session_expired") === "1"
    );
  });
  // Consume the persistent flag AFTER the first paint so the banner has time
  // to render (before this fix, consuming during render caused a race with
  // <ProtectedRoute> Navigate cycles that stripped the banner).
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.sessionStorage?.getItem("salnes_session_expired") === "1"
    ) {
      try {
        window.sessionStorage.removeItem("salnes_session_expired");
      } catch {
        /* ignore */
      }
    }
  }, []);
  // Only accept safe internal targets ("/app/*" or "/datos-abertos" etc.)
  const safeRedirect = /^\/(?!\/)[a-zA-Z0-9/\-_?=&]*$/.test(redirect) ? redirect : "/app/dashboard";

  if (user) return <Navigate to={safeRedirect} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success(t("dashboard.welcome"));
      nav(safeRedirect, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail || err?.message || t("login.error_generic");
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-salnes-ivory">
      {/* Left: form */}
      <div className="flex flex-col justify-between p-8 lg:p-14">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-salnes-stone hover:text-salnes-atlantic"
            data-testid="login-back-home"
          >
            <ArrowLeft size={14} /> {t("login.back_home")}
          </Link>
          <LanguageSelector />
        </div>

        <div className="max-w-md mx-auto w-full">
          <div className="flex items-center gap-3 mb-8">
            <BrandMark size={40} />
            <div>
              <div className="font-serif text-lg text-salnes-ink" data-testid="login-brand-name">{t("brand.name")}</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone">
                {t("brand.sub_id")}
              </div>
            </div>
          </div>
          <h1 className="font-serif text-4xl text-salnes-ink">{t("login.title")}</h1>
          <p className="mt-3 text-salnes-stone text-sm max-w-sm">
            {t("login.subtitle")}
          </p>

          {expired && (
            <div
              className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              data-testid="login-expired-banner"
            >
              <div className="font-medium">
                {t("login.expired_title", "A túa sesión expirou")}
              </div>
              <div className="text-[12px] text-amber-800 mt-0.5">
                {t(
                  "login.expired_sub",
                  "Volve iniciar sesión para continuar. Os tokens caducan cada 7 días."
                )}
              </div>
            </div>
          )}

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                placeholder="admin@salnes.gal"
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="login-submit"
              className="w-full h-11 rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90 text-white"
            >
              {submitting ? t("login.loading") : t("login.submit")}
              <LogIn size={15} className="ml-2" />
            </Button>
          </form>
        </div>

        <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone">
          {t("footer.copyright", { year: new Date().getFullYear() })}
        </div>
      </div>

      {/* Right: visual */}
      <div className="hidden lg:block relative">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "url(/brand/hero-salnes-alt1.jpg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-salnes-atlantic/70 via-salnes-ink/60 to-salnes-emerald/40" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="text-[11px] uppercase tracking-[0.28em] text-white/70 mb-3">
            {t("login.visual_eyebrow")}
          </div>
          <div className="font-serif text-3xl leading-tight max-w-md">
            {t("login.visual_quote")}
          </div>
        </div>
      </div>
    </div>
  );
}
